/*
 * Columnist Panel — self-service dashboard for a user who has been
 * assigned as a columnist by an admin. The user can manage their own
 * profile (photo, bio, social links) and their own articles.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import MediaPicker from "@/components/admin/MediaPicker";
import { toast } from "sonner";
import { UserCircle, FileText, Plus, X, Save, Trash2 } from "lucide-react";

function slugify(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .replace(/ı/g, "i").replace(/ğ/g, "g").replace(/ü/g, "u")
    .replace(/ş/g, "s").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function ColumnistPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [columns, setColumns] = useState([]);
  const emptyCol = { title: "", slug: "", content: "", cover_image: "", is_active: true };
  const [cf, setCf] = useState(emptyCol);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (!user) { navigate("/giris"); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/columnists/me");
        if (cancelled) return;
        setMe(r.data);
        setProfile({
          name: r.data.name || "",
          slug: r.data.slug || "",
          photo: r.data.photo || "",
          bio: r.data.bio || "",
          province_id: r.data.province_id || null,
          social: r.data.social || {},
          is_active: r.data.is_active !== false,
          user_id: r.data.user_id || user.id,
        });
        const cr = await api.get("/columns?mine=true&limit=500");
        if (!cancelled) setColumns(cr.data || []);
      } catch {
        if (!cancelled) setMe(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, navigate]);

  const saveProfile = async () => {
    if (!me) return;
    try {
      await api.put(`/columnists/${me.id}`, profile);
      toast.success("Profil güncellendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const loadColumns = async () => {
    const cr = await api.get("/columns?mine=true&limit=500");
    setColumns(cr.data || []);
  };

  const saveColumn = async () => {
    if (!me) return;
    if (!cf.title?.trim()) return toast.error("Başlık zorunlu");
    if (!cf.content?.trim()) return toast.error("İçerik zorunlu");
    try {
      const payload = {
        ...cf,
        columnist_id: me.id,
        slug: cf.slug || slugify(cf.title),
        province_id: me.province_id || null,
      };
      if (editingId) await api.put(`/columns/${editingId}`, payload);
      else await api.post("/columns", payload);
      setCf(emptyCol);
      setEditingId(null);
      await loadColumns();
      toast.success(editingId ? "Yazı güncellendi" : "Yazı yayınlandı");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const editColumn = (c) => {
    setEditingId(c.id);
    setCf({
      title: c.title || "",
      slug: c.slug || "",
      content: c.content || "",
      cover_image: c.cover_image || "",
      is_active: c.is_active !== false,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteColumn = async (id) => {
    if (!window.confirm("Bu yazıyı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/columns/${id}`);
      if (editingId === id) { setEditingId(null); setCf(emptyCol); }
      await loadColumns();
      toast.success("Yazı silindi");
    } catch {
      toast.error("Silinemedi");
    }
  };

  if (loading) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-gray-500">Yükleniyor...</div>;
  }

  if (!me) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10 text-center" data-testid="columnist-panel-empty">
        <UserCircle className="w-14 h-14 mx-auto text-gray-300" />
        <h1 className="text-2xl font-black mt-3">Köşe Yazarı Değilsiniz</h1>
        <p className="text-sm text-gray-600 mt-2">
          Hesabınız henüz bir yazar profili ile eşleştirilmemiş. Yönetici sizi köşe
          yazarı olarak atadığında bu sayfa aktifleşir.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="columnist-panel">
      <div className="flex items-center gap-3 mb-5">
        <img
          src={me.photo || "https://api.dicebear.com/7.x/initials/svg?seed=" + encodeURIComponent(me.name)}
          alt=""
          className="w-12 h-12 rounded-full bg-gray-100 object-cover"
        />
        <div>
          <h1 className="text-xl md:text-2xl font-black">{me.name}</h1>
          <div className="text-xs text-gray-500">Köşe Yazarı Paneli</div>
        </div>
      </div>

      <div className="flex gap-2 mb-4 border-b">
        <button
          onClick={() => setTab("profile")}
          data-testid="cp-tab-profile"
          className={`px-3 py-2 text-sm font-semibold ${tab === "profile" ? "border-b-2 border-[#e11d2a] text-[#e11d2a]" : "text-gray-600"}`}
        >
          <UserCircle className="w-4 h-4 inline mr-1" /> Profil
        </button>
        <button
          onClick={() => setTab("columns")}
          data-testid="cp-tab-columns"
          className={`px-3 py-2 text-sm font-semibold ${tab === "columns" ? "border-b-2 border-[#e11d2a] text-[#e11d2a]" : "text-gray-600"}`}
        >
          <FileText className="w-4 h-4 inline mr-1" /> Yazılarım ({columns.length})
        </button>
      </div>

      {tab === "profile" ? (
        <div className="bg-white border rounded-lg p-4 space-y-3 max-w-2xl" data-testid="cp-profile">
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Ad Soyad</label>
            <input
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Profil Fotoğrafı</label>
            <MediaPicker
              value={profile.photo}
              onChange={(u) => setProfile({ ...profile, photo: u })}
              scope="columnists"
              testId="cp-photo-picker"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Biyografi</label>
            <textarea
              rows={4}
              value={profile.bio}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {["twitter", "instagram", "facebook", "linkedin", "website"].map((k) => (
              <div key={k}>
                <label className="block text-xs font-medium mb-1 text-gray-600 capitalize">{k}</label>
                <input
                  value={profile.social?.[k] || ""}
                  onChange={(e) => setProfile({ ...profile, social: { ...(profile.social || {}), [k]: e.target.value } })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  placeholder="https://..."
                />
              </div>
            ))}
          </div>
          <button
            onClick={saveProfile}
            className="px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold flex items-center gap-1"
            data-testid="cp-save-profile"
          >
            <Save className="w-4 h-4" /> Profili Kaydet
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold">{editingId ? "Yazıyı Düzenle" : "Yeni Yazı"}</h3>
              {editingId && (
                <button
                  onClick={() => { setEditingId(null); setCf(emptyCol); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <input
              value={cf.title}
              onChange={(e) => setCf({ ...cf, title: e.target.value, slug: cf.slug || slugify(e.target.value) })}
              placeholder="Başlık"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
              data-testid="cp-column-title"
            />
            <input
              value={cf.slug}
              onChange={(e) => setCf({ ...cf, slug: slugify(e.target.value) })}
              placeholder="Slug (URL)"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
            />
            <MediaPicker
              value={cf.cover_image}
              onChange={(u) => setCf({ ...cf, cover_image: u })}
              scope="columns"
              testId="cp-column-cover"
            />
            <textarea
              rows={10}
              value={cf.content}
              onChange={(e) => setCf({ ...cf, content: e.target.value })}
              placeholder="Yazınızın metnini buraya yazın. Paragrafları boş satırla ayırın."
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
              data-testid="cp-column-content"
            />
            <label className="text-xs text-gray-600 inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={cf.is_active !== false}
                onChange={(e) => setCf({ ...cf, is_active: e.target.checked })}
              />
              Yayınla
            </label>
            <button
              onClick={saveColumn}
              className="w-full px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold inline-flex items-center justify-center gap-1"
              data-testid="cp-save-column"
            >
              <Plus className="w-4 h-4" />
              {editingId ? "Güncelle" : "Yayınla"}
            </button>
          </div>

          <div className="bg-white border rounded-lg p-4 max-h-[600px] overflow-y-auto space-y-2">
            {columns.length === 0 && (
              <div className="text-sm text-gray-400 text-center py-6">Henüz yazınız yok</div>
            )}
            {columns.map((c) => (
              <div
                key={c.id}
                className={`border rounded p-2 flex items-center gap-2 ${editingId === c.id ? "bg-amber-50 border-amber-200" : ""}`}
                data-testid={`cp-column-item-${c.id}`}
              >
                {c.cover_image && (
                  <img src={c.cover_image} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{c.title}</div>
                  <div className="text-[10px] text-gray-500">
                    {c.is_active === false ? "Taslak" : "Yayında"}
                    {c.publish_at ? " • " + new Date(c.publish_at).toLocaleDateString("tr-TR") : ""}
                  </div>
                </div>
                <button onClick={() => editColumn(c)} className="text-xs text-blue-600 hover:underline">
                  Düzenle
                </button>
                <button
                  onClick={() => deleteColumn(c.id)}
                  className="text-red-500 hover:bg-red-50 p-1 rounded"
                  title="Sil"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
